// Transform OpenWeatherMap One Call 3.0 data to match the app's existing data structure
// This ensures Current.js, HourlyChart.js, and DailyForecast.js require ZERO changes

// OWM weather ID to human-readable condition text
const owmCodeToCondition = (weatherId) => {
  if (!weatherId) return 'Clear';
  
  const conditions = {
    // Thunderstorm group (2xx)
    200: 'Thunderstorm', 201: 'Thunderstorm', 202: 'Thunderstorm',
    210: 'Thunderstorm', 211: 'Thunderstorm', 212: 'Thunderstorm', 221: 'Thunderstorm',
    230: 'Thunderstorm', 231: 'Thunderstorm', 232: 'Thunderstorm',
    // Drizzle group (3xx)
    300: 'Light Drizzle', 301: 'Drizzle', 302: 'Heavy Drizzle',
    310: 'Light Rain', 311: 'Rain', 312: 'Heavy Rain',
    313: 'Showers', 314: 'Heavy Showers', 321: 'Showers',
    // Rain group (5xx)
    500: 'Light Rain', 501: 'Rain', 502: 'Heavy Rain', 503: 'Heavy Rain', 504: 'Heavy Rain',
    511: 'Freezing Rain',
    520: 'Light Showers', 521: 'Showers', 522: 'Heavy Showers', 531: 'Showers',
    // Snow group (6xx)
    600: 'Light Snow', 601: 'Snow', 602: 'Heavy Snow',
    611: 'Sleet', 612: 'Sleet', 613: 'Sleet',
    615: 'Light Rain', 616: 'Rain',
    620: 'Light Snow Showers', 621: 'Snow Showers', 622: 'Snow Showers',
    // Atmosphere group (7xx)
    701: 'Fog', 711: 'Fog', 721: 'Fog', 731: 'Fog',
    741: 'Fog', 751: 'Fog', 761: 'Fog', 762: 'Fog',
    771: 'Cloudy', 781: 'Thunderstorm',
    // Clear (800)
    800: 'Clear',
    // Clouds (80x)
    801: 'Mainly Clear',
    802: 'Partly Cloudy',
    803: 'Cloudy',
    804: 'Overcast'
  };
  
  return conditions[weatherId] || 'Clear';
};

// Helper: Convert m/s to mph
const msToMph = (ms) => Math.round((ms || 0) * 2.237);

// Helper: Convert Celsius to Fahrenheit
const cToF = (c) => Math.round((c * 9/5) + 32);

// Helper: Determine if a unix timestamp is during daytime
const isDaytime = (dt, sunrise, sunset) => {
  return dt >= sunrise && dt <= sunset ? 1 : 0;
};

// Helper: Convert unix timestamp + timezone offset to ISO string
const unixToISO = (timestamp, tzOffsetSeconds) => {
  const d = new Date((timestamp + tzOffsetSeconds) * 1000);
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const hours = String(d.getUTCHours()).padStart(2, '0');
  const minutes = String(d.getUTCMinutes()).padStart(2, '0');
  const seconds = String(d.getUTCSeconds()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

// Helper: Convert unix timestamp to HH:MM:SS string in location timezone
const unixToTimeString = (timestamp, tzOffsetSeconds) => {
  const d = new Date((timestamp + tzOffsetSeconds) * 1000);
  const hours = String(d.getUTCHours()).padStart(2, '0');
  const minutes = String(d.getUTCMinutes()).padStart(2, '0');
  const seconds = String(d.getUTCSeconds()).padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
};

// Helper: Get thunder probability from weather code
const getThunderProb = (weatherId) => {
  if (weatherId >= 200 && weatherId < 210) return 90;
  if (weatherId >= 210 && weatherId < 220) return 80;
  if (weatherId >= 220 && weatherId < 233) return 70;
  return 0;
};

// Main transform function - converts OWM proxy response to app data structure
export const transformOWMData = (apiResponse) => {
  const { location, onecall, hourly_aqi } = apiResponse;
  const oc = onecall;
  const tzOffset = oc.timezone_offset || 0;
  
  if (!oc.current || !oc.daily || !oc.hourly) {
    throw new Error('Incomplete data from OpenWeatherMap');
  }
  
  const current = oc.current;
  const daily = oc.daily;
  const hourly = oc.hourly;
  
  // Find today's sunrise/sunset for current weather is_day
  const todaySunrise = daily[0]?.sunrise || current.sunrise;
  const todaySunset = daily[0]?.sunset || current.sunset;
  
  // ============================================================
  // Transform CURRENT weather
  // ============================================================
  const weatherId = current.weather?.[0]?.id || 800;
  const conditionText = owmCodeToCondition(weatherId);
  const isDay = isDaytime(current.dt, todaySunrise, todaySunset);
  
  const transformedCurrent = {
    temp_c: Math.round(current.temp),
    temp_f: cToF(current.temp),
    condition: {
      text: conditionText
    },
    wind_mph: msToMph(current.wind_speed),
    wind_dir: current.wind_deg || 0,
    is_day: isDay,
    humidity: current.humidity || 0,
    uvIndex: Math.round(current.uvi || 0),
    feelsLikeTemp: Math.round(current.feels_like || current.temp),
    cloudiness: current.clouds || 0
  };
  
  // ============================================================
  // Transform LOCATION
  // ============================================================
  const transformedLocation = {
    name: location.name || 'Unknown',
    admin_area: location.admin_area || '',
    country: location.country || 'Unknown',
    localtime: unixToISO(current.dt, tzOffset),
    timezone: location.timezone || oc.timezone || 'UTC',
    lat: location.lat,
    lon: location.lon
  };
  
  // ============================================================
  // Transform DAILY forecast (8 days from OWM)
  // ============================================================
  const transformedDaily = daily.map((day) => {
    const dayWeatherId = day.weather?.[0]?.id || 800;
    const dayCondition = owmCodeToCondition(dayWeatherId).toLowerCase();
    const isSnowDay = dayCondition.includes('snow') || dayCondition.includes('sleet');
    const isRainDay = dayCondition.includes('rain') || dayCondition.includes('drizzle') || 
                      dayCondition.includes('shower') || dayCondition.includes('thunderstorm');
    
    const pop = Math.round((day.pop || 0) * 100); // OWM pop is 0-1, convert to 0-100
    const rainMm = day.rain || 0;
    const snowMm = day.snow || 0;
    
    // Split precipitation probability between rain and snow
    let rainChance = 0;
    let snowChance = 0;
    
    if (isSnowDay) {
      snowChance = pop;
      if (rainMm > 0 && snowMm > 0) {
        const total = rainMm + snowMm;
        rainChance = Math.round(pop * (rainMm / total));
        snowChance = pop - rainChance;
      }
    } else if (isRainDay) {
      rainChance = pop;
    } else if (pop > 0) {
      // Has precipitation probability but condition doesn't clearly indicate type
      if (snowMm > 0) {
        snowChance = pop;
      } else {
        rainChance = pop;
      }
    }
    
    // Convert sunrise/sunset unix timestamps to simple time strings (HH:MM:SS)
    // Current.js expects simple time format like "06:40:14" — NOT full ISO strings
    // because it correctly parses "HH:MM" format directly without re-applying timezone
    const sunriseTime = unixToTimeString(day.sunrise, tzOffset);
    const sunsetTime = unixToTimeString(day.sunset, tzOffset);
    
    return {
      date: unixToISO(day.dt, tzOffset).split('T')[0],
      day: {
        maxtemp_f: cToF(day.temp.max),
        mintemp_f: cToF(day.temp.min),
        maxtemp_c: Math.round(day.temp.max),
        mintemp_c: Math.round(day.temp.min),
        condition: {
          text: owmCodeToCondition(dayWeatherId)
        },
        daily_chance_of_rain: rainChance,
        daily_chance_of_snow: snowChance,
        sunriseTime: sunriseTime,
        sunsetTime: sunsetTime,
        sunrise: sunriseTime,
        sunset: sunsetTime,
        humidity: day.humidity || 0,
        uvIndex: Math.round(day.uvi || 0),
        maxWindSpeed: msToMph(day.wind_speed),
        windDir: day.wind_deg || 0,
        rainAccumulation: rainMm, // mm
        snowAccumulation: snowMm, // mm (OWM reports snow in mm water equivalent)
        summary: day.summary || '' // OWM AI summary if available
      }
    };
  });
  
  // ============================================================
  // Transform HOURLY forecast (48 hours from OWM)
  // ============================================================
  const now = Math.floor(Date.now() / 1000);
  
  const transformedHourly = hourly
    .filter(hour => hour.dt >= now - 3600) // Include current hour
    .slice(0, 48)
    .map((hour) => {
      const hourWeatherId = hour.weather?.[0]?.id || 800;
      const hourCondition = owmCodeToCondition(hourWeatherId).toLowerCase();
      
      // Determine day/night for this specific hour
      // Find the right day's sunrise/sunset
      let hourIsDay = 1;
      for (const day of daily) {
        const dayDate = unixToISO(day.dt, tzOffset).split('T')[0];
        const hourDate = unixToISO(hour.dt, tzOffset).split('T')[0];
        if (dayDate === hourDate) {
          hourIsDay = isDaytime(hour.dt, day.sunrise, day.sunset);
          break;
        }
      }
      // Fallback: use today's sunrise/sunset
      if (hourIsDay === undefined) {
        hourIsDay = isDaytime(hour.dt, todaySunrise, todaySunset);
      }
      
      // Precipitation splitting
      const precipProb = Math.round((hour.pop || 0) * 100);
      const hourRainMm = hour.rain?.['1h'] || 0;
      const hourSnowMm = hour.snow?.['1h'] || 0;
      
      let rainChance = 0;
      let snowChance = 0;
      
      if (hourCondition.includes('snow') || hourCondition.includes('sleet')) {
        snowChance = precipProb;
      } else if (hourCondition.includes('rain') || hourCondition.includes('drizzle') || 
                 hourCondition.includes('shower') || hourCondition.includes('thunderstorm')) {
        rainChance = precipProb;
      } else if (precipProb > 0) {
        if (hourSnowMm > 0) {
          snowChance = precipProb;
        } else if (hourRainMm > 0) {
          rainChance = precipProb;
        }
        // If no accumulation but there's probability, don't show it (clear/cloudy conditions)
      }
      
      return {
        time: unixToISO(hour.dt, tzOffset),
        temp_c: Math.round(hour.temp),
        temp_f: cToF(hour.temp),
        condition: {
          text: owmCodeToCondition(hourWeatherId)
        },
        chance_of_rain: rainChance,
        chance_of_snow: snowChance,
        rain_amount: hourRainMm,
        snow_amount: hourSnowMm,
        wind_speed: msToMph(hour.wind_speed),
        wind_gust: msToMph(hour.wind_gust),
        is_day: hourIsDay,
        feelsLikeTemp: Math.round(hour.feels_like || hour.temp),
        cloudiness: hour.clouds || 0,
        visibility: hour.visibility || 10000,
        pressure: hour.pressure || 0,
        humidity: hour.humidity || 0,
        uvIndex: Math.round(hour.uvi || 0),
        dewPoint: Math.round(hour.dew_point || 0),
        thunderProb: getThunderProb(hourWeatherId),
        windDir: hour.wind_deg || 0
      };
    });
  
  // ============================================================
  // Group hourly data by day for forecast structure
  // ============================================================
  const forecastDays = transformedDaily.map(day => {
    const dayHours = transformedHourly.filter(hour => {
      if (!hour) return false;
      const hourDate = hour.time.split('T')[0];
      return hourDate === day.date;
    });
    
    return {
      ...day,
      hour: dayHours
    };
  });
  
  // ============================================================
  // Build AQI data (from separate air pollution API)
  // ============================================================
  // hourly_aqi comes pre-processed from PHP proxy with US EPA AQI values
  
  return {
    location: transformedLocation,
    current: transformedCurrent,
    forecast: {
      forecastday: forecastDays
    },
    hourlyMasterTable: transformedHourly,
    hourly_aqi: hourly_aqi || [],
    alerts: oc.alerts || []
  };
};

// Backward compatibility alias
export const transformForecaData = transformOWMData;
